import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode, MAX_COMPARE } from "./BrandModeContext";
import DealTemplateModal from "./modals/DealTemplateModal";
import { DELIVERABLES, EMPTY_VALUE, fmtMoney, fmtMoneyCompact, fmtRange, fullName } from "./brandUtils";
import { fitStrength } from "./brandDealUtils";
import {
  Avatar,
  Badge,
  Btn,
  Card,
  Checkbox,
  Chip,
  ErrorState,
  Loading,
  Overline,
  PageHeader,
  SearchBox,
  SegmentedControl,
} from "./ui/BrandUI";
import Icon from "./ui/icons";

// Where an athlete plays, for a row or a card: their school (the
// institution their profile leads with), else their club or pro team (the
// row's `team`), so a club or pro athlete never shows a blank.
export const schoolOrTeam = (athlete) => athlete?.school || athlete?.team || "";

// ---------------------------------------------------------- Filter chips
// Single-value chip backed by a native select (keyboard + mobile friendly).
const SelectChip = ({ value, onChange, options, allLabel }) => (
  <span className="relative inline-flex">
    <Chip chevron brand={!!value} className="pointer-events-none">
      {value || allLabel}
    </Chip>
    <select
      aria-label={allLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="absolute inset-0 cursor-pointer opacity-0"
    >
      <option value="">{allLabel}</option>
      {(options || []).map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </span>
);

// Multi-value chip with a checkbox popover ("States 2", "Deal type 2").
const MultiChip = ({ label, values, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <span className="relative inline-flex" ref={ref}>
      <Chip chevron brand={values.length > 0} count={values.length || undefined} onClick={() => setOpen((o) => !o)}>
        {label}
      </Chip>
      {open && (
        <div className="bm-fade-in absolute left-0 top-12 z-30 max-h-72 w-60 overflow-y-auto rounded-[12px] border border-bm-border bg-white p-2 shadow-xl">
          {(options || []).length === 0 && <p className="px-2 py-2 bm-body-s text-bm-muted">No options yet</p>}
          {(options || []).map((opt) => {
            const v = typeof opt === "string" ? opt : opt.value;
            const text = typeof opt === "string" ? opt : opt.label;
            return (
              <div key={v} className="rounded-[8px] px-2 py-[6px] hover:bg-bm-subtle">
                <Checkbox checked={values.includes(v)} onChange={() => toggle(v)} label={text} className="w-full" />
              </div>
            );
          })}
          {values.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="mt-1 w-full rounded-[8px] px-2 py-[6px] text-left bm-label-xs text-bm-orange hover:bg-bm-subtle">
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </span>
  );
};

// -------------------------------------------------------------- Pieces
// The row checkbox has no visible label, so it is named after the athlete.
const CompareCheck = ({ checked, onChange, name }) => (
  <Checkbox checked={checked} onChange={onChange} aria-label={`Compare ${name || "athlete"}`} />
);

const ShortlistButton = ({ shortlisted, onClick }) => (
  <Btn variant={shortlisted ? "tertiary" : "secondary"} size="sm" onClick={onClick} className="min-w-[92px]">
    {shortlisted ? "✓ Shortlisted" : "+ Shortlist"}
  </Btn>
);

const FitCell = ({ athlete }) => {
  const top = athlete.brandFit?.[0];
  if (!top) return <span className="bm-body-m text-bm-tertiary">{EMPTY_VALUE}</span>;
  const strength = fitStrength(top.score);
  return (
    <span className="flex items-center gap-[10px]">
      <span className="bm-body-m text-bm-ink capitalize">{top.category || "Lifestyle"}</span>
      <Badge tone="outline" dot={strength.dot}>
        {strength.label}
      </Badge>
    </span>
  );
};

const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== "...") pages.push("...");
  }
  return (
    <nav aria-label="Pagination" className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="Previous page" className="flex h-8 w-8 items-center justify-center rounded-full border border-bm-border text-bm-muted disabled:opacity-40">
        <Icon name="chevron-left" size={14} />
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`gap-${i}`} aria-hidden="true" className="px-1 bm-body-s text-bm-tertiary">...</span>
        ) : (
          <button key={p} type="button" onClick={() => onChange(p)} aria-label={`Page ${p}`} aria-current={p === page ? "page" : undefined} className={`h-8 min-w-[32px] rounded-full px-2 bm-label-xs ${p === page ? "bg-bm-ink text-white" : "border border-bm-border text-bm-ink hover:bg-bm-subtle"}`}>
            {p}
          </button>
        ),
      )}
      <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages} aria-label="Next page" className="flex h-8 w-8 items-center justify-center rounded-full border border-bm-border text-bm-muted disabled:opacity-40">
        <Icon name="chevron-right" size={14} />
      </button>
    </nav>
  );
};

// ---------------------------------------------------------- Table view
const DiscoverTable = ({ athletes }) => {
  const { isCompared, toggleCompare, shortlistHas, toggleShortlist } = useBrandMode();
  return (
    <Card padding="p-0" className="overflow-x-auto">
      <table className="w-full min-w-[1000px] border-collapse">
        <thead>
          <tr>
            <th className="bm-th w-[56px]" />
            <th className="bm-th w-[300px]">Athlete</th>
            <th className="bm-th w-[140px]">Reach</th>
            <th className="bm-th w-[110px] !text-right">Score</th>
            <th className="bm-th w-[170px]">CacheValue</th>
            <th className="bm-th w-[200px]">Top brand fit</th>
            <th className="bm-th" />
          </tr>
        </thead>
        <tbody>
          {athletes.map((athlete) => {
            const compared = isCompared(athlete.athleteId);
            const shortlisted = shortlistHas(athlete.athleteId);
            return (
              <tr key={athlete.athleteId} className={`h-14 ${compared ? "bg-bm-orange/5" : "bg-white"}`}>
                <td className="bm-td">
                  <CompareCheck checked={compared} onChange={() => toggleCompare(athlete.athleteId)} name={fullName(athlete)} />
                </td>
                <td className="bm-td">
                  <span className="flex items-center gap-[10px]">
                    <Avatar name={athlete.name} surname={athlete.surname} src={athlete.avatar} size={32} />
                    <span className="flex min-w-0 flex-col gap-[2px]">
                      <span className="truncate bm-h-xs text-bm-ink">{fullName(athlete)}</span>
                      <span className="truncate bm-body-s text-bm-muted">
                        {[athlete.position, schoolOrTeam(athlete)].filter(Boolean).join(" · ") || EMPTY_VALUE}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="bm-td">
                  <Badge tone="outline">{athlete.visTier}</Badge>
                </td>
                <td className="bm-td text-right bm-num-m text-bm-ink">{athlete.cacheScore}</td>
                <td className="bm-td bm-body-m text-bm-ink">{fmtRange(athlete.cacheValue) || <span className="text-bm-tertiary">{EMPTY_VALUE}</span>}</td>
                <td className="bm-td">
                  <FitCell athlete={athlete} />
                </td>
                <td className="bm-td text-right">
                  <ShortlistButton shortlisted={shortlisted} onClick={() => toggleShortlist(athlete.athleteId)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};

// ----------------------------------------------------------- Grid view
export const AthleteMedia = ({ athlete, tall = false, children, className = "" }) => (
  <div className={`bm-media relative flex flex-col justify-between overflow-hidden ${tall ? "h-[507px] p-5 pb-[22px]" : "h-[304px] px-4 pb-4 pt-[14px]"} ${className}`}>
    {athlete.avatar && (
      <img src={athlete.avatar} alt="" className="absolute inset-0 h-full w-full object-cover opacity-90" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
    <div className="relative flex items-start justify-between">
      {athlete.sport ? (
        <Badge tone="soft" className="uppercase">{athlete.sport}</Badge>
      ) : (
        <span />
      )}
      <span className={`flex items-center justify-center rounded-full bg-white bm-num-m text-bm-ink ${tall ? "h-11 w-11" : "h-9 w-9"}`}>
        {athlete.cacheScore}
      </span>
    </div>
    <div className="relative flex flex-col gap-1 text-white">
      <span className="bm-body-s text-white/70">
        {[athlete.jersey && `#${athlete.jersey}`, athlete.position].filter(Boolean).join(" · ") || " "}
      </span>
      <span className={tall ? "bm-h-m" : "bm-h-s"}>{fullName(athlete)}</span>
      <span className="bm-body-s text-white/70">
        {[schoolOrTeam(athlete), athlete.state].filter(Boolean).join(" · ") || EMPTY_VALUE}
      </span>
      <span className="mt-1">
        <Badge tone="onDark">{athlete.visTier}</Badge>
      </span>
    </div>
    {children}
  </div>
);

const GridCard = ({ athlete }) => {
  const { isCompared, toggleCompare, shortlistHas, toggleShortlist } = useBrandMode();
  const compared = isCompared(athlete.athleteId);
  const shortlisted = shortlistHas(athlete.athleteId);
  return (
    <div className={`flex flex-col overflow-hidden rounded-[12px] border bg-white ${compared ? "border-bm-orange ring-1 ring-bm-orange" : "border-bm-border"}`}>
      <AthleteMedia athlete={athlete} />
      <div className="flex flex-col gap-1 px-4 pt-4 pb-3">
        <Overline>CacheValue</Overline>
        <span className="bm-num-m text-bm-ink">{fmtRange(athlete.cacheValue) || EMPTY_VALUE}</span>
      </div>
      <div className="mt-auto flex items-center justify-between border-t border-bm-mutedbg px-4 py-3">
        <button type="button" onClick={() => toggleCompare(athlete.athleteId)} className={`bm-label-xs ${compared ? "text-bm-orange" : "text-bm-muted hover:text-bm-ink"}`}>
          {compared ? "✓ Comparing" : "Compare"}
        </button>
        <Btn variant={shortlisted ? "tertiary" : "secondary"} size="sm" onClick={() => toggleShortlist(athlete.athleteId)}>
          {shortlisted ? "✓ In shortlist" : "+ Shortlist"}
        </Btn>
      </div>
    </div>
  );
};

// ------------------------------------------------------- Shortlist tray
const ShortlistTray = () => {
  const navigate = useNavigate();
  const { shortlist, athleteById, applyDealTemplate, createDraftFromShortlist } = useBrandMode();
  const [templateOpen, setTemplateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const items = shortlist.items || [];
  const stats = shortlist.stats || {};
  if (!items.length) return null;

  const saveAsCampaign = async () => {
    setSaving(true);
    try {
      const created = await createDraftFromShortlist();
      toast.success("Draft campaign created from your shortlist.");
      navigate(`/brand/campaigns/${created.id}/edit`);
    } catch (err) {
      toast.error(err.message || "Could not create the campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="p-[22px]" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Overline>Shortlist</Overline>
          <Badge tone="dark">{items.length}</Badge>
          <span className="bm-body-m text-bm-muted">
            {stats.estMax ? `${fmtMoneyCompact(stats.estMin)}–${fmtMoneyCompact(stats.estMax)} estimated` : "add deliverables to estimate"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="secondary" size="sm" onClick={() => setTemplateOpen(true)}>
            Deal template
          </Btn>
          <Btn variant="secondary" size="sm" onClick={saveAsCampaign} disabled={saving}>
            {saving ? "Saving..." : "Save as campaign"}
          </Btn>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => {
          const a = item.athlete || athleteById[item.athleteId] || {};
          return (
            <button
              type="button"
              key={item.athleteId}
              onClick={() => navigate("/brand/shortlist")}
              className="flex h-12 items-center gap-3 rounded-[12px] border border-bm-border bg-bm-subtle px-4 text-left hover:bg-bm-mutedbg"
            >
              <Avatar name={a.name} surname={a.surname} src={a.avatar} size={28} />
              <span className="min-w-0 flex-1 truncate bm-label-l text-bm-ink">{fullName(a) || "Athlete"}</span>
              <span className="bm-body-m text-bm-muted">{item.offer ? fmtMoney(item.offer) : EMPTY_VALUE}</span>
            </button>
          );
        })}
      </div>
      <p className="bm-body-s text-bm-tertiary">
        Compare and shortlist stay with you as you filter and browse. Nothing resets until you send offers.
      </p>
      {templateOpen && (
        <DealTemplateModal
          athleteCount={items.length}
          onClose={() => setTemplateOpen(false)}
          onApply={async (mix) => {
            setTemplateOpen(false);
            await applyDealTemplate(mix);
            toast.success("Deal template applied to all athletes.");
          }}
        />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------- Page
// The table/grid choice is remembered per browser. Storage can be blocked
// (private mode, site data off) and then every access throws, so both reads
// and writes are guarded and the page falls back to the table view.
const VIEW_KEY = "bm.discover.view";
const VIEWS = ["table", "grid"];

const readStoredView = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_KEY);
    return VIEWS.includes(stored) ? stored : "table";
  } catch {
    return "table";
  }
};

const storeView = (view) => {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Storage unavailable: the choice lasts for this visit only.
  }
};

const DiscoverPage = () => {
  const navigate = useNavigate();
  const {
    filteredPool,
    options,
    poolLoading,
    poolError,
    filters,
    setFilters,
    clearFilters,
    compareIds,
    comparedAthletes,
    clearCompare,
    shortlist,
    addToShortlist,
    shortlistHas,
  } = useBrandMode();
  const [view, setView] = useState(readStoredView);
  const [page, setPage] = useState(1);
  const pageSize = view === "grid" ? 50 : 100;
  const totalPages = Math.max(1, Math.ceil(filteredPool.length / pageSize));

  useEffect(() => setPage(1), [filters, view]);
  useEffect(() => storeView(view), [view]);

  const pagedPool = useMemo(
    () => filteredPool.slice((page - 1) * pageSize, page * pageSize),
    [filteredPool, page, pageSize],
  );
  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const shortlistCount = shortlist.items?.length || 0;

  const addAllCompared = () => {
    const fresh = comparedAthletes.filter((a) => !shortlistHas(a.athleteId));
    if (!fresh.length) return toast.info("Everyone selected is already shortlisted.");
    addToShortlist(fresh.map((a) => a.athleteId));
    return toast.success(`${fresh.length} athlete${fresh.length === 1 ? "" : "s"} added to shortlist.`);
  };

  return (
    <div>
      <PageHeader
        title="Discover"
        subtitle="Find, compare and package athletes. Everything you select stays with you until you send offers."
        actions={
          <Btn onClick={() => navigate("/brand/shortlist")}>
            {shortlistCount ? `Open shortlist · ${shortlistCount}` : "Open shortlist"}
          </Btn>
        }
      />

      <div className="flex flex-col gap-4 pt-7">
        {/* Filter bar */}
        <Card padding="px-5 py-[18px]" className="flex flex-wrap items-center gap-3">
          <SelectChip value={filters.sport} onChange={setFilter("sport")} options={options?.sports} allLabel="All sports" />
          <SelectChip value={filters.gender} onChange={setFilter("gender")} options={options?.genders} allLabel="All genders" />
          <MultiChip label="States" values={filters.states || []} onChange={setFilter("states")} options={options?.states} />
          <SelectChip value={filters.tier} onChange={setFilter("tier")} options={options?.tiers} allLabel="All tiers" />
          <MultiChip
            label="Deal type"
            values={filters.dealTypes || []}
            onChange={setFilter("dealTypes")}
            options={DELIVERABLES.map((d) => ({ value: d.key, label: d.label }))}
          />
          <SearchBox aria-label="Search athletes" value={filters.search} onChange={setFilter("search")} placeholder="Search name or school" className="w-[226px]" />
          <button type="button" onClick={clearFilters} className="bm-label-l text-bm-ink hover:text-bm-orange">
            Clear all
          </button>
          <SegmentedControl value={view} onChange={setView} options={VIEWS} className="ml-auto" />
        </Card>

        {/* Compare bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex -space-x-2">
              {comparedAthletes.slice(0, 3).map((a) => (
                <Avatar key={a.athleteId} name={a.name} surname={a.surname} src={a.avatar} size={28} className="ring-2 ring-bm-mutedbg" />
              ))}
            </span>
            <span className="bm-label-l text-bm-ink">
              {compareIds.length} selected to compare
            </span>
            <span className="bm-body-s text-bm-tertiary">up to {MAX_COMPARE}</span>
          </div>
          <div className="flex items-center gap-2">
            <Btn variant="secondary" size="sm" onClick={clearCompare} disabled={!compareIds.length}>
              Clear
            </Btn>
            <Btn variant="secondary" size="sm" onClick={addAllCompared} disabled={!compareIds.length}>
              Add all to shortlist
            </Btn>
            <Btn size="sm" onClick={() => navigate("/brand/compare")} disabled={compareIds.length < 2}>
              Compare{compareIds.length ? ` ${compareIds.length}` : ""}
            </Btn>
          </div>
        </div>

        {/* Results */}
        {poolLoading ? (
          <Loading label="Loading athletes..." />
        ) : poolError ? (
          <ErrorState>{poolError}</ErrorState>
        ) : filteredPool.length === 0 ? (
          <Card className="py-16 text-center bm-body-m text-bm-muted">
            No athletes match these filters. Try clearing a filter.
          </Card>
        ) : view === "table" ? (
          <DiscoverTable athletes={pagedPool} />
        ) : (
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
            {pagedPool.map((athlete) => (
              <GridCard key={athlete.athleteId} athlete={athlete} />
            ))}
          </div>
        )}
        {filteredPool.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="bm-body-s text-bm-muted">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredPool.length)} of {filteredPool.length}
            </span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}

        <ShortlistTray />

        <p className="bm-body-s text-bm-tertiary">Figures are estimated market ranges, not offers or quotes.</p>
      </div>
    </div>
  );
};

export default DiscoverPage;
